<template>
  <div>
    <div class="container">
      <h3 class="page-title">AI Provider API Keys</h3>

      <p class="section-caption">
        Optionally use your own key. {{ brand.name }} will use it for AI features instead of ours, and your
        provider bills you directly for that usage. Keys are stored encrypted and can never be read back.
      </p>

      <v-list lines="two" class="rounded-lg">
        <v-list-item v-for="provider in providers" :key="provider.key">
          <template #prepend>
            <v-icon :color="statusOf(provider).color">{{ statusOf(provider).icon }}</v-icon>
          </template>

          <v-list-item-title>
            {{ provider.label }}
            <v-chip
              v-if="hasKey(provider)"
              :color="statusOf(provider).color"
              size="x-small"
              variant="tonal"
              class="ml-2"
            >
              {{ statusOf(provider).label }}
            </v-chip>
          </v-list-item-title>

          <v-list-item-subtitle v-if="editing !== provider.key" class="text-wrap">
            <code v-if="hasKey(provider)" class="ai-key-mask">{{ mask(provider) }}</code>
            <span v-else>Not configured — {{ brand.name }}'s key is used</span>
          </v-list-item-subtitle>

          <div v-if="editing === provider.key" class="mt-2 mb-1">
            <div class="d-flex align-start ga-2">
              <v-text-field
                v-model="apiKey"
                :label="`${provider.label} API key`"
                :placeholder="provider.placeholder"
                type="password"
                autocomplete="off"
                density="compact"
                variant="outlined"
                hide-details="auto"
                style="max-width: 320px"
                :error-messages="error"
                :disabled="saving"
                @update:model-value="error = null"
                @keyup.enter="onSave(provider)"
              />
            </div>
            <a :href="provider.keyUrl" target="_blank" rel="noopener" class="ai-key-help">
              Where do I find my {{ provider.label }} API key?
            </a>
          </div>

          <template #append>
            <div v-if="editing === provider.key" class="d-flex align-center ga-2">
              <v-btn
                size="small"
                variant="outlined"
                min-width="104"
                prepend-icon="mdi-content-save"
                :disabled="!apiKey.trim()"
                :loading="saving"
                @click="onSave(provider)"
              >
                Save
              </v-btn>
              <v-btn
                size="small"
                variant="outlined"
                min-width="104"
                prepend-icon="mdi-close"
                :disabled="saving"
                @click="cancel"
              >
                Cancel
              </v-btn>
            </div>

            <div v-else class="d-flex align-center ga-2">
              <v-btn
                v-if="hasKey(provider)"
                size="small"
                variant="outlined"
                min-width="104"
                prepend-icon="mdi-refresh"
                :loading="checking === provider.key"
                @click="onCheck(provider)"
              >
                Check
              </v-btn>
              <v-btn
                size="small"
                variant="outlined"
                min-width="104"
                :prepend-icon="hasKey(provider) ? 'mdi-pencil' : 'mdi-plus'"
                @click="startEdit(provider)"
              >
                {{ hasKey(provider) ? 'Replace' : 'Add key' }}
              </v-btn>
              <v-btn
                v-if="hasKey(provider)"
                size="small"
                variant="outlined"
                color="error"
                min-width="104"
                prepend-icon="mdi-close"
                :loading="removing === provider.key"
                @click="onRemove(provider)"
              >
                Remove
              </v-btn>
            </div>
          </template>
        </v-list-item>
      </v-list>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useBrand } from '@/composables/useBrand'

const store = useStore()
const brand = useBrand()

onMounted(() => store.dispatch('user/getCurrentUser'))

const providers = [
  {
    key: 'OPENAI',
    label: 'OpenAI',
    placeholder: 'sk-...',
    keyUrl: 'https://platform.openai.com/api-keys'
  },
  {
    key: 'GEMINI',
    label: 'Gemini',
    placeholder: 'AIza...',
    keyUrl: 'https://aistudio.google.com/apikey'
  }
]

const currentUser = computed(() => store.state.user.user)
const keyFor = (provider) => (currentUser.value?.aiProviderKeys || []).find((k) => k.provider === provider.key)

const hasKey = (provider) => !!keyFor(provider)
const mask = (provider) => keyFor(provider)?.mask

const STATUSES = {
  ACTIVE: { icon: 'mdi-check-circle', color: 'success', label: 'Active' },
  REJECTED: { icon: 'mdi-alert-circle', color: 'error', label: 'Rejected' },
  QUOTA_EXCEEDED: { icon: 'mdi-credit-card-off-outline', color: 'warning', label: 'No credits' }
}

const statusOf = (provider) => {
  if (!hasKey(provider)) {
    return { icon: 'mdi-key-outline', color: 'medium-emphasis' }
  }

  return STATUSES[keyFor(provider)?.status] || STATUSES.ACTIVE
}

const apiKey = ref('')
const editing = ref(null)
const saving = ref(false)
const removing = ref(null)
const checking = ref(null)
const error = ref(null)

const startEdit = (provider) => {
  editing.value = provider.key
  apiKey.value = ''
  error.value = null
}

const cancel = () => {
  editing.value = null
  apiKey.value = ''
  error.value = null
}

const onSave = async (provider) => {
  if (!apiKey.value.trim() || saving.value) {
    return
  }

  saving.value = true
  error.value = null
  try {
    const result = await store.dispatch('user/setAiProviderKey', {
      provider: provider.key,
      apiKey: apiKey.value.trim()
    })

    if (result?.status && result.status !== 'REJECTED') {
      cancel()

      return
    }

    error.value = result?.message || 'The provider rejected this API key.'
  } finally {
    saving.value = false
  }
}

const onCheck = async (provider) => {
  checking.value = provider.key
  try {
    await store.dispatch('user/checkAiProviderKey', { provider: provider.key })
  } finally {
    checking.value = null
  }
}

const onRemove = async (provider) => {
  removing.value = provider.key
  try {
    await store.dispatch('user/removeAiProviderKey', { provider: provider.key })
    cancel()
  } finally {
    removing.value = null
  }
}
</script>

<style scoped>
.page-title {
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: 700;
  color: var(--dark-4, #363A40);
}

.section-caption {
  margin-bottom: 32px;
  font-size: 13px;
  max-width: 620px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity, 0.6));
}

.ai-key-mask {
  font-size: 12px;
}

.ai-key-help {
  display: inline-block;
  font-size: 12px;
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}

.ai-key-help:hover {
  text-decoration: underline;
}
</style>
